import { EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source';

import type { SendUserQueryData } from 'Services/motorway/services/agent-core/agentCore.types';

const AI_COMPANION_POST_USER_QUERY_STREAM_ENDPOINT = '/api/ai-companion/send-user-query?stream=true';

interface SendUserQueryStreamCallbacks {
	onClose: () => void;
	onConversationId: (data: { conversationId: string; isNewConversation: boolean }) => void;
	onError: (error: Error) => void;
	onResponse: (data: SendUserQueryData & { conversationId: string }) => void;
}

interface SendUserQueryStreamParams {
	callbacks: SendUserQueryStreamCallbacks;
	data: { conversationId: string | null; query: string };
	signal?: AbortSignal;
}

class FatalStreamError extends Error {}

export const sendUserQueryStream = async ({ callbacks, data, signal }: SendUserQueryStreamParams): Promise<void> => {
	let hasReceivedResponse = false;

	await fetchEventSource(AI_COMPANION_POST_USER_QUERY_STREAM_ENDPOINT, {
		body: JSON.stringify(data),
		headers: { 'Content-Type': 'application/json' },
		method: 'POST',
		// Server intentionally closes after sending the response — suppress default retry behaviour
		onclose() {
			if (!hasReceivedResponse) {
				callbacks.onClose();
			}
		},
		onerror(error) {
			callbacks.onError(error instanceof Error ? error : new Error('Stream connection failed'));
			throw new FatalStreamError();
		},
		onmessage(event) {
			const payload = JSON.parse(event.data);

			switch (event.event) {
				case 'conversationId':
					callbacks.onConversationId(payload);
					break;
				case 'response':
					hasReceivedResponse = true;
					callbacks.onResponse(payload);
					break;
				case 'error':
					callbacks.onError(new Error(payload.message));
					break;
				default:
					break;
			}
		},
		async onopen(response) {
			if (response.ok && response.headers.get('content-type')?.includes(EventStreamContentType)) {
				return;
			}

			throw new FatalStreamError(`Stream request failed: ${response.status}`);
		},
		openWhenHidden: true,
		signal,
	});
};
